import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Compass, Award, Globe, Mail } from "lucide-react";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SectionBreak from "@/components/sections/SectionBreak";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { syncToMailerLite } from "@/lib/mailerlite";

const TravelBuildsResilience = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [showGdprDialog, setShowGdprDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "How Travel Builds Resilience in Children: A Doctor's Perspective | Rebecca Ryell Travel";
    const meta = document.querySelector('meta[name="description"]');
    const content = "How travel builds resilience in children — a doctor's perspective on family adventure travel, confidence, curiosity and raising world-ready kids.";
    if (meta) {
      meta.setAttribute("content", content);
    } else {
      const tag = document.createElement("meta");
      tag.name = "description";
      tag.content = content;
      document.head.appendChild(tag);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!gdprConsent) {
      setShowGdprDialog(true);
      return;
    }
    await submitEmail();
  };

  const submitEmail = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert([{ email: email.trim(), source: "Pillar_Resilience" }]);
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already on the list!");
        } else {
          throw error;
        }
      } else {
        syncToMailerLite(email.trim(), "Pillar_Resilience");
        setEmail("");
        navigate("/thank-you");
      }
    } catch (error) {
      console.error("Error joining list:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resilienceCards = [
    {
      icon: Compass,
      title: "Travel Introduces Manageable Uncertainty",
      text: "Children experience unfamiliar foods, languages and environments — and learn they can handle it. Each small unknown they navigate builds a quiet inner confidence that no classroom lesson can replicate.",
    },
    {
      icon: Award,
      title: "Travel Builds Confidence Through Small Challenges",
      text: "Ordering food in a foreign language, navigating public transport, adapting to a new sleeping arrangement — these micro-challenges compound over time into genuine self-belief.",
    },
    {
      icon: Globe,
      title: "Travel Expands Perspective",
      text: "Children see first-hand that the world works in many different ways. They learn that 'different' is not 'wrong', and that curiosity is a superpower worth protecting.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 md:pt-32 pb-16 md:pb-24">
        <article className="container mx-auto px-4 max-w-3xl">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-sm text-muted-foreground hover:text-accent transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Hero Header */}
          <header className="mb-12">
            <span className="font-mono text-xs uppercase tracking-widest text-accent mb-3 block">
              Family Travel Guide
            </span>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 leading-tight">
              How Travel Builds Resilience in Children: A Doctor's Perspective
            </h1>
            <p className="font-mono text-sm text-muted-foreground">
              By Dr Rebecca Ryell · March 2025
            </p>
          </header>

          <SectionBreak />

          {/* Introduction */}
          <section className="mb-12">
            <p className="text-foreground/80 text-lg leading-relaxed mb-4">
              As both a GP and a mother who has travelled extensively with my own children, I've seen first-hand how travel shapes the way children see the world — and themselves.
            </p>
            <p className="text-foreground/80 text-lg leading-relaxed">
              Family travel is not just about holidays. It can play a powerful role in helping children develop resilience, curiosity and confidence in unfamiliar situations. This guide draws on my experience as a doctor, a parent and a family adventure travel coach to explore why travel matters so much for our children's development.
            </p>
          </section>

          {/* What Is Resilience? */}
          <section className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-4">
              What Is Resilience in Children?
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              Resilience is a child's ability to adapt, cope with challenges and remain curious in unfamiliar situations. It's not about being fearless — it's about learning that discomfort is temporary, that uncertainty can be navigated, and that they are capable of more than they think. Resilience in children is one of the most important foundations we can nurture as parents.
            </p>
          </section>

          <SectionBreak />

          {/* How Travel Builds Resilience */}
          <section className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-8">
              How Travel Builds Resilience
            </h2>
            <div className="grid gap-6">
              {resilienceCards.map((card) => (
                <div key={card.title} className="scrapbook-card p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                      <card.icon className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-primary mb-2">
                        {card.title}
                      </h3>
                      <p className="text-foreground/80 leading-relaxed">
                        {card.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* The Experiences Children Remember */}
          <section className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-4">
              The Experiences Children Remember
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              "But they won't remember it." It's the most common objection I hear from parents considering travel with young children. And it's understandable — but it misses something important.
            </p>
            <p className="text-foreground/80 leading-relaxed mb-4">
              Children may not remember every detail of a trip, but travel shapes things far more fundamental than memory:
            </p>
            <ul className="space-y-2 ml-6 text-foreground/80 leading-relaxed list-disc">
              <li><strong className="text-foreground">Identity</strong> — who they believe they are and what they're capable of</li>
              <li><strong className="text-foreground">Confidence</strong> — a deep-seated belief that they can handle the unfamiliar</li>
              <li><strong className="text-foreground">Worldview</strong> — how they see other people, cultures and ways of living</li>
            </ul>
          </section>

          <SectionBreak />

          {/* Making Space for Meaningful Experiences */}
          <section className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-4">
              Making Space for Meaningful Experiences
            </h2>
            <div className="bg-secondary/10 border-l-4 border-secondary rounded-r-lg p-6 my-6">
              <p className="font-display text-lg font-semibold text-primary mb-3">
                The Why Behind Travel, Not Just the Where
              </p>
              <p className="text-foreground/80 leading-relaxed mb-3">
                The best family adventures sit just outside your comfort zone.
              </p>
              <p className="text-foreground/80 leading-relaxed">
                Not reckless. Not overwhelming. But far enough to spark curiosity, courage and connection. That's where the magic happens — in the space between the familiar and the unknown, experienced together as a family.
              </p>
            </div>
          </section>

          {/* Real Examples */}
          <section className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-4">
              Real Examples from Our Family
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              This isn't theory. Our family has tested this approach first-hand across some genuinely adventurous journeys:
            </p>
            <ul className="space-y-2 ml-6 text-foreground/80 leading-relaxed list-disc">
              <li>A round-the-world trip with a newborn</li>
              <li>Backpacking through Sumatra with young children</li>
              <li>A campervan road trip across the United States</li>
              <li>Long-term travel across Southeast Asia as a family</li>
              <li>Home exchanges in countries we'd never visited before</li>
            </ul>
            <p className="text-foreground/80 leading-relaxed mt-4">
              Every one of these trips taught our children something a textbook never could — and taught us something about parenting we didn't expect to learn.
            </p>
          </section>

          <SectionBreak />

          {/* How Parents Can Start */}
          <section className="mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-4">
              How Parents Can Start
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              You don't need to book a round-the-world trip tomorrow. Here are three principles to begin:
            </p>
            <ol className="space-y-4 ml-6 text-foreground/80 leading-relaxed list-decimal">
              <li>
                <strong className="text-foreground">Start small.</strong> A weekend camping trip, a ferry to a new country, a night in a city you've never explored. The distance doesn't matter — the unfamiliarity does.
              </li>
              <li>
                <strong className="text-foreground">Embrace uncertainty.</strong> Resist the urge to plan every moment. Leave space for the unscripted — that's where the real learning happens.
              </li>
              <li>
                <strong className="text-foreground">Focus on experiences, not perfection.</strong> The best family travel memories rarely come from the Instagram moments. They come from the challenges you navigated together.
              </li>
            </ol>
          </section>

          {/* Newsletter CTA */}
          <section className="scrapbook-card p-8 md:p-10 text-center mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-3">
              Ready to Travel Bravely?
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-6 max-w-lg mx-auto">
              If you're dreaming about bigger adventures with your children but aren't sure where to start, join the Adventure List for ideas, inspiration and practical guidance.
            </p>
            <form onSubmit={handleSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-2" data-form="adventure-list">
              <div className="flex-1 relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 py-6 text-base bg-card/90 border-border"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono uppercase tracking-wider shadow-lg hover:shadow-xl transition-all py-6"
              >
                {isLoading ? "Joining..." : "Get the Newsletter"}
              </Button>
            </form>

            <AlertDialog open={showGdprDialog} onOpenChange={setShowGdprDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-primary">Before you join…</AlertDialogTitle>
                  <AlertDialogDescription className="text-foreground leading-relaxed">
                    By signing up you agree to our{" "}
                    <a href="/privacy" className="text-accent underline" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{" "}
                    and to receive adventure updates. You can unsubscribe at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="font-mono text-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono text-sm"
                    onClick={() => {
                      setGdprConsent(true);
                      submitEmail();
                    }}
                  >
                    I Agree — Get the Newsletter
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </section>

          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-sm text-muted-foreground hover:text-accent transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default TravelBuildsResilience;
